import Paper from '@mui/material/Paper'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'

import { toFilterOptions } from '@util/filter'

import { useAuthenticatedUser } from '../../../auth/hooks/useAuthenticatedUser'
import { useCombiOpdrachtenSearchStore } from '../../../opdrachten/combi/hooks/useCombiOpdrachtenSearchStore'
import { useOpdrachtenSearchStore } from '../../../opdrachten/hooks/useOpdrachtenSearchStore'
import { useNavigate } from '../../../router/hooks/useNavigate'
import { Page } from '../../../theme/components/Page'
import { PageSection } from '../../../theme/components/PageSection'
import { useAansluitingValuelists } from '../../../valuelists/hooks/useAansluitingValuelists'
import { useValuelists } from '../../../valuelists/hooks/useValuelists'
import { useActiviteitenSearchStore } from '../../activiteiten/hooks/useActiviteitenSearchStore'
import { useAssignedBijlagenSearchStore } from '../../bijlagen/hooks/useAssignedBijlagenSearchStore'
import { useProjectenSearchStore } from '../../projecten/hooks/useProjectenSearchStore'
import { useSubprocessenSearchStore } from '../../subprocessen/hooks/useSubprocessenSearchStore'
import { Kpis } from '../components/kpis/Kpis'
import { Subprocessen } from '../components/subprocessen/Subprocessen'
import { Werkbakken } from '../components/werkbakken/Werkbakken'
import { Werklijsten } from '../components/werklijsten/Werklijsten'
import { useSelectedTabStore } from '../hooks/useSelectedTabStore'
import { useWerklijsten } from '../hooks/useWerklijsten'

const getSelectedTabByAuth = (
  selectedTab: string,
  canViewCombiOpdrachten: boolean,
  canViewOpdrachten: boolean,
  canViewSubprocessen: boolean,
) => {
  if (selectedTab) return selectedTab

  if (canViewCombiOpdrachten) return 'processtappen'
  if (canViewOpdrachten) return 'werkbakken'
  if (canViewSubprocessen) return 'subprocessen'

  return ''
}

export const Dashboard = () => {
  const navigate = useNavigate()
  const { data: valuelists } = useValuelists()
  const { data: aansluitingValuelists } = useAansluitingValuelists()
  const { canViewOpdrachten, canViewCombiOpdrachten } = useAuthenticatedUser()

  const filterOptions = {
    ...valuelists,
    ...aansluitingValuelists,
    labels: toFilterOptions(aansluitingValuelists.labels),
  }

  const {
    data: { subprocessen },
  } = useWerklijsten()

  const canViewSubprocessen = !!subprocessen.length

  const { selectedTab, storeSelectedTab } = useSelectedTabStore()
  const selectedTabByAuth = getSelectedTabByAuth(
    selectedTab,
    canViewCombiOpdrachten,
    canViewOpdrachten,
    canViewSubprocessen,
  )

  const { storeQuery: storeQueryActiviteiten, query: queryActiviteiten } =
    useActiviteitenSearchStore()
  const {
    storeQuery: storeQueryAssignedBijlagen,
    query: queryAssignedBijlagen,
  } = useAssignedBijlagenSearchStore()
  const { storeQuery: storeQueryProjecten } = useProjectenSearchStore()
  const { storeQuery: storeQuerySubprocessen } = useSubprocessenSearchStore()
  const { storeQuery: storeQueryCombiOpdrachten } =
    useCombiOpdrachtenSearchStore()
  const { storeQuery: storeQueryOpdrachten } = useOpdrachtenSearchStore()

  const handleTabChange = (
    _event: React.SyntheticEvent<Element, Event>,
    newTab: string,
  ) => {
    storeSelectedTab(newTab)
  }

  const onClickTile = (query, url) => {
    if (url === '/aansluiting/projecten') storeQueryProjecten(query)
    if (url === '/combiopdrachten') storeQueryCombiOpdrachten(query)
    if (url === '/opdrachten') storeQueryOpdrachten(query)
    if (url === '/aansluiting/subprocessen') storeQuerySubprocessen(query)
    if (url === '/aansluiting/bijlagen')
      storeQueryAssignedBijlagen({
        ...query,
        sort: queryAssignedBijlagen.sort,
      })
    if (url === '/aansluiting/activiteiten')
      storeQueryActiviteiten({
        ...query,
        sort: queryActiviteiten.sort,
      })

    navigate(url)
  }

  return (
    <Page
      title={['Dashboard', 'Aansluiting']}
      breadcrumbs={[
        { label: 'Aansluiting', href: '/home', tooltip: 'CAPO home' },
        { label: 'Dashboard' },
      ]}
      fillScreen
    >
      <PageSection id="dashboard" heading="Dashboard" />

      <Tabs centered value={selectedTabByAuth} onChange={handleTabChange}>
        {/* KPIs and Processtappen have to be seperate as Tabs wants an array */}
        {canViewCombiOpdrachten && (
          <Tab label="Processtappen" value="processtappen" />
        )}
        {canViewCombiOpdrachten && <Tab label="KPI's" value="kpis" />}

        {canViewOpdrachten && <Tab label="Werkbakken" value="werkbakken" />}

        {canViewCombiOpdrachten && (
          <Tab label="Werklijsten" value="werklijsten" />
        )}
        {canViewSubprocessen && (
          <Tab label="Subprocessen" value="subprocessen" />
        )}
      </Tabs>

      <Paper>
        {['processtappen', 'kpis'].includes(selectedTabByAuth) &&
          canViewCombiOpdrachten && (
            <Kpis
              selectedTab={selectedTabByAuth}
              filterOptions={filterOptions}
              onClickTile={onClickTile}
            />
          )}

        {selectedTabByAuth === 'werkbakken' && canViewOpdrachten && (
          <Werkbakken onClickTile={onClickTile} filterOptions={filterOptions} />
        )}

        {selectedTabByAuth === 'werklijsten' && canViewCombiOpdrachten && (
          <Werklijsten
            onClickTile={onClickTile}
            filterOptions={filterOptions}
          />
        )}

        {selectedTabByAuth === 'subprocessen' && (
          <Subprocessen
            onClickTile={onClickTile}
            filterOptions={filterOptions}
          />
        )}
      </Paper>
    </Page>
  )
}
